<script setup lang="ts">
import { computed } from 'vue'

import {
  isCaptain,
  LineupModel,
  Lineups,
  LineupsTitle,
  useLineupModel,
  useSeasonSquad,
} from '@collector/desktop-feature-lineups'
import { Modal, runLoader } from '@collector/shared-ui'
import { Participant, SubParticipant } from '@collector/sportsapi-types'
import {
  useEventId,
  useParticipants,
  useSeason,
  useSubParticipants,
} from '@desktop/views/Relation/composables'
import {
  UpdateLineupsFn,
  useLineupConfig,
  useProbableLineupDeferredClear,
} from '@desktop/views/Relation/Sports/composables'
import {
  cloneLineupModel,
  keepWillNotPlaySubParticipants,
  subParticipantsKeptAfterLineupClear,
} from '@desktop/views/Relation/Sports/composables/probable-lineup-deferred-clear.utils'

import ClearProbableLineupOverlay from './Overlays/ClearProbableLineupOverlay.vue'

interface Props {
  participant: Participant
  confirmUpdateLineup: (
    lineup: LineupModel,
    initialLineup: LineupModel,
    updateLineups: UpdateLineupsFn,
    captain?: SubParticipant | null,
    isPendingDeferredClear?: boolean,
  ) => Promise<void>
}
const props = defineProps<Props>()

type Emits = { close: [] }
const emit = defineEmits<Emits>()

const { season } = useSeason()
const eventId = useEventId()
const { homeParticipant, awayParticipant } = useParticipants()
const { homeSubParticipants, awaySubParticipants } = useSubParticipants()
const { isParticipantPendingDeferredClear } = useProbableLineupDeferredClear()

const {
  config,
  createLineupModel,
  updateLineups,
  toSortedLineupModel,
  hasPositionChanged,
  sortSquad,
  transformSubParticipants,
} = useLineupConfig()

function subParticipantsForParticipant(): SubParticipant[] {
  const participantId = props.participant.id
  if (homeParticipant.value?.id === participantId)
    return homeSubParticipants.value
  if (awayParticipant.value?.id === participantId)
    return awaySubParticipants.value

  return []
}

async function loadLineupSubParticipants(
  players: SubParticipant[],
): Promise<SubParticipant[]> {
  const source = isParticipantPendingDeferredClear(props.participant.id)
    ? keepWillNotPlaySubParticipants(players)
    : players

  if (transformSubParticipants)
    return transformSubParticipants(eventId.value, source)

  return source
}

const { lineup, initialLineup, updateLineupModel, fetchLineup } =
  useLineupModel(
    eventId,
    () => props.participant.id,
    config.value.triggerSortOnLineupModelUpdate ?? true,
    createLineupModel,
    toSortedLineupModel,
    loadLineupSubParticipants,
  )

const { seasonSquadSubParticipants, fetchSeasonSquad } = useSeasonSquad(
  season,
  () => props.participant,
  { compareFn: sortSquad },
)

const hasChanges = computed(() => {
  const keys = Object.keys(lineup.value) as PropertyKey[]

  return keys.some((key) =>
    hasPositionChanged(key, lineup.value, initialLineup.value),
  )
})

const captain = computed(
  () => Object.values(lineup.value).flat().find(isCaptain) ?? null,
)

const isPendingDeferredClear = computed(() =>
  isParticipantPendingDeferredClear(props.participant.id),
)

const hasLineupContent = computed(() =>
  Object.values(lineup.value).some((players) => players.length > 0),
)

const canSave = computed(() => {
  if (isPendingDeferredClear.value) return hasLineupContent.value

  return hasChanges.value
})

async function confirmSave(): Promise<void> {
  await props.confirmUpdateLineup(
    lineup.value,
    initialLineup.value,
    updateLineups,
    captain.value,
    isPendingDeferredClear.value,
  )
}

function playersKeptAfterClear(): SubParticipant[] {
  return subParticipantsKeptAfterLineupClear(
    Object.values(lineup.value).flat(),
    subParticipantsForParticipant(),
  )
}

async function handleProbableLineupClearedLocally(): Promise<void> {
  const kept = playersKeptAfterClear()
  const players = transformSubParticipants
    ? await transformSubParticipants(eventId.value, kept)
    : kept
  const clearedLineup = toSortedLineupModel(createLineupModel(players))
  lineup.value = clearedLineup
  initialLineup.value = cloneLineupModel(lineup.value)
}

async function loadLineupForModal(): Promise<void> {
  if (isParticipantPendingDeferredClear(props.participant.id)) {
    await handleProbableLineupClearedLocally()
    return
  }

  await fetchLineupWhenNotPendingDeferredClear()
}

async function fetchLineupWhenNotPendingDeferredClear(): Promise<void> {
  if (isParticipantPendingDeferredClear(props.participant.id)) {
    await handleProbableLineupClearedLocally()
    return
  }

  await fetchLineup()
}

await runLoader(Promise.all([fetchSeasonSquad(), loadLineupForModal()]))
</script>

<template>
  <Modal
    :confirmButton="{
      text: 'Save',
      confirm: confirmSave,
      disabled: !canSave,
    }"
    :withCloseShortcuts="false"
    @close="emit('close')"
  >
    <template #title>
      <LineupsTitle
        :participant
        @close="emit('close')"
      />
    </template>
    <div class="relative @xs:w-[77vw] @lg:w-[49.125rem]">
      <Lineups
        class="w-full"
        :modelValue="lineup"
        :config
        :squad="seasonSquadSubParticipants"
        @update:modelValue="updateLineupModel"
      />
      <ClearProbableLineupOverlay
        :participant
        @clear="handleProbableLineupClearedLocally"
      />
      <slot
        name="overlay"
        :fetchLineup="fetchLineupWhenNotPendingDeferredClear"
      />
    </div>
  </Modal>
</template>
